import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EditSearchComponent } from '@root/search/components/edit-search/edit-search.component';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  selectIsSingleCounty,
  selectMySearchesTemplates,
  selectPreferenceElement,
  selectPreferenceValue,
  selectPremiumSearchMinimumZoom,
  selectSearchField,
  selectSearchFieldsArray,
  selectSelectedGeos,
  selectTemplate
} from '@store/user/user.selector';
import { debounceTime, distinctUntilChanged, filter, first, map, shareReplay, startWith, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DataElement, PreferenceCode, Template, TemplateCode, TemplateType } from '@store/user/user.model';
import { ModalType, MULTI_MODAL_MY_SEARCH } from '@shared/models/modal.model';
import { RlstValidators } from '@shared/validators/validators';
import { selectedMapLayer } from '@store/map-layers.selector';
import { OperatorValue, RENDER_ID, SEARCH_RESULTS_COUNT_DEBOUNCE_TIME, SearchField, SearchOperator, Shape } from '@store/search-board/search-board.model';
import {
  selectDrawingShapes,
  selectDrawingShapesForSearch,
  selectIsPremiumViewAllowed,
  selectMapZoom,
  selectMySearchFieldsData,
  selectSelectedSearch,
  selectSubmitFormTrigger
} from '@store/search-board/search-board.selector';
import {
  ClearAllPremium,
  ClearSearchResultsCount,
  GetSearchResultsCount,
  MySearch,
  RemoveDrawingFigure,
  SaveValuesOfMySearch,
  SetMySearchLastLoadedTemplate,
  SetPremiumLayer,
  SetSelectedMySearch,
  SetSelectedMySearchModal,
  SetShowSelected
} from '@store/search-board/search-board.actions';
import { ConfirmationService, ConfModalResponse } from '@shared/services/confirmation.service';
import { FieldCode, PremiumCrossFieldCodes, PremiumFieldCodes } from '@root/search/models/display-fields.constant';
import { DatePipe } from '@angular/common';
import { SaveCustomMySearch, UpdateCustomMySearch } from '@store/user/user.actions';
import { StringUtils } from '@shared/utils/string-utils';
import { MapUtils } from '@shared/utils/map-utils';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { RemoveCurrentPropertyIndex } from '@store/reports/reports.actions';
import { PREMIUM_MAP_LAYER_NONE } from '@root/search/models/map.constants';

@Component({
  selector: 'rlst-my-search',
  templateUrl: './my-search.component.html',
  styleUrls: ['./my-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MySearchComponent implements OnInit {
  @Output() clearSearchResults: EventEmitter<void> = new EventEmitter<void>();
  public mySearchForm: UntypedFormGroup;
  public mySearchList: Array<Template>;
  public premiumMapTemplate: Template;
  public searchFields$: BehaviorSubject<Array<DataElement>> = new BehaviorSubject([]);
  public defaultActionName: string = 'Create New Search';
  public mapShapes$: Observable<Array<Shape>>;
  public mapShapesForSearch$: Observable<Array<Array<string>>>;
  public selectedSearch$: Observable<Template | null>;
  public FieldCode: typeof FieldCode = FieldCode;
  public isFormEmpty: boolean = false;
  public readonly isFormEmpty$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public isFormValid$: Observable<boolean>;
  public defaultOperatorValueListMap: { [key: string]: Array<OperatorValue> } = {};
  private templateNames: Array<{ label: string, type: string }> = [];
  private countyList: Array<string>;
  private selectedSearch: string;
  private premiumOptionSelected: SearchField;
  private searchTemplate: Template;
  public isPremiumLayerWorks$: Observable<boolean>;
  private currentLayerOperatorValueKey: string;
  public isSingleCounty$: Observable<boolean>;
  public crossFields: Array<FieldCode>;
  public premiumMinScaleExceeded$: Observable<boolean>;
  private lastSelectedPremiumOption: SearchField;
  public savedSearchSelected: string;
  private lastOpenedTemplateCode: string | null = null;
  private formDestroy$: Subject<void> = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly store$: Store<AppState>,
    private readonly fb: UntypedFormBuilder,
    private readonly dialog: MatDialog,
    private readonly confirmationService: ConfirmationService,
    private readonly datePipe: DatePipe,
    private readonly mixpanelService: MixpanelService,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.store$.dispatch(new ClearSearchResultsCount());
    this.isPremiumLayerWorks$ = this.store$
      .pipe(
        select(selectPreferenceValue(PreferenceCode.PG_RL_PREMIUM_SEARCH, PreferenceCode.PREF_RL_PREMIUM_SEARCH)),
        withLatestFrom(
          this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TOOLS,
            PreferenceCode.PREF_PREMIUM_MAP_LAYERS), takeUntilDestroyed(this.destroyRef)))
        ),
        map(([searchValue, mapValue]) => {
          return searchValue !== 'N' && mapValue.hasAccess;
        }),
        takeUntilDestroyed(this.destroyRef)
      );
    this.isSingleCounty$ = this.store$.pipe(select(selectIsSingleCounty), takeUntilDestroyed(this.destroyRef));
    this.getMySearchesSortedList();

    this.selectedSearch$ = this.store$.pipe(select(selectSelectedSearch));
    this.mapShapes$ = this.store$.pipe(select(selectDrawingShapes));
    this.mapShapesForSearch$ = this.store$.pipe(select(selectDrawingShapesForSearch));

    // Single subscription for selected search changes — handles both initial load and template switches
    this.selectedSearch$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((template: Template) => {
      if (template) {
        this.selectedSearch = template.label;
        this.searchTemplate = template;
      }
      this.openSearch(template);
    });

    // clearSearchResults re-opens the current template (resets form values)
    this.clearSearchResults.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.searchTemplate) {
        this.lastOpenedTemplateCode = null; // force re-create
        this.openSearch(this.searchTemplate, true);
      }
    });

    this.store$.pipe(
      select(selectSubmitFormTrigger),
      filter(Boolean),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.search());

    this.store$.select(selectSelectedGeos).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      this.countyList = result.map((x) => x.fips);
      if (this.countyList.length > 1) {
        this.store$.dispatch(new ClearAllPremium(true));
        this.premiumOptionSelected = null;
      }
    });

    // Map layer changes should NOT rebuild the form — just trigger a recount
    this.store$.pipe(
      select(selectedMapLayer),
      distinctUntilChanged(),
      filter(() => !!this.searchTemplate),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.search(true);
    });

    this.store$.pipe(
      select(selectIsPremiumViewAllowed),
      withLatestFrom(
        this.store$.pipe(select(selectedMapLayer)),
        this.store$.pipe(select(selectIsSingleCounty))
      ),
      takeUntilDestroyed(this.destroyRef))
      .subscribe(([value, layer, isSingle]) => {
        if (!isSingle && !value && (layer && layer.length)) {
          this.store$.dispatch(new SetPremiumLayer(PREMIUM_MAP_LAYER_NONE));
          return;
        }
      });

    this.premiumMinScaleExceeded$ = combineLatest([
      this.store$.pipe(select(selectMapZoom)),
      this.store$.pipe(select(selectPremiumSearchMinimumZoom))
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      shareReplay(),
      map(([mapZoom, premiumSearchMinimumZoom]) => {
        return premiumSearchMinimumZoom > mapZoom;
      })
    );

    const state = history.state;
    if(state && state.isDashboard && !state.createSearch){
      this.openMySearch(state.template);
      history.replaceState({
        isDashboard: false,
        template: '',
        createSearch: false
      }, '');
    } else if (state && state.isDashboard && state.createSearch){
      this.openEditSearchModal(true);
      history.replaceState({
        isDashboard: false,
        template: '',
        createSearch: false
      }, '');
    }
  }

  private getDataFromTemplate(isSearch: boolean = true): Observable<Array<SearchField>> {
    return combineLatest(
      [
        this.selectedSearch$,
        this.mapShapesForSearch$
      ])
      .pipe(
        take(1),
        map(([template, shapesValues]: [Template, Array<Array<string>>]) => {
          if (template) {
            const searchConfig = template.dataElementList;
            let searchFields: Array<SearchField> = [];
            for (const field in this.mySearchForm.controls) {
              if (this.mySearchForm.controls.hasOwnProperty(field)) {
                const dataElement: DataElement = searchConfig.find((element: DataElement) => element.fieldCode === field);
                const formArray: UntypedFormArray = this.mySearchForm.get(field) as UntypedFormArray;
                const operatorValueList: Array<OperatorValue> = [];

                for (const control of formArray.controls) {
                  const operatorControl: AbstractControl = control.get('operator');
                  const valueControl: AbstractControl = control.get(['values', 'valueFrom']);
                  const toValueControl: AbstractControl = control.get(['values', 'valueTo']);

                  if (dataElement && (valueControl.value || toValueControl.value || !isSearch)) {
                    const isMultiField = this.checkIfMultiField(dataElement);
                    if (isMultiField && !valueControl.value[0] && isSearch) {
                      continue;
                    }
                    const operatorValue: OperatorValue = {
                      ...(this.defaultOperatorValueListMap[dataElement.fieldCode] || dataElement.operatorValueList)
                        .find((operator: OperatorValue) => operator.operator === operatorControl.value)
                    };

                    if (operatorValue) {
                      if (isMultiField) {
                        valueControl.value.forEach((value: string) => {
                          const newOperator = {...operatorValue};
                          newOperator.values = isSearch ? [this.formatLookUpValues(value)] : [value || ''];
                          operatorValueList.push(newOperator);
                        });
                        continue;
                      } else {
                        if (dataElement.fieldCode === FieldCode.OWNER_NAME_1 && valueControl.value.toString().includes(',') && isSearch) {
                          const valueArray = valueControl.value.toString().split(',');
                          const firstName = valueArray[1].trim();
                          const lastName = valueArray[0].trim();
                          operatorValue.values = [`${lastName} ${firstName}`];
                        } else {
                          operatorValue.values = [this.formatValues(valueControl.value) || ''];
                        }
                      }

                      if (toValueControl && toValueControl.value) {
                        operatorValue.values.push(this.formatValues(toValueControl.value) || '');
                      }

                      operatorValueList.push(operatorValue);
                    }
                  }
                }

                if (operatorValueList.length) {
                  if (dataElement.fieldCode === FieldCode.ALL_APN && isSearch) {
                    searchFields.push({fieldCode: FieldCode.APN, operatorValues: operatorValueList});
                    searchFields.push({fieldCode: FieldCode.ALTERNATE_APN, operatorValues: operatorValueList});
                    searchFields.push({fieldCode: FieldCode.UNFORMATTTED_APN, operatorValues: operatorValueList});
                  } else {
                    searchFields.push({fieldCode: field, operatorValues: operatorValueList});
                  }
                }
              }
            }

            if (shapesValues && shapesValues.length) {
              searchFields = searchFields.filter((field: SearchField) => field.fieldCode !== FieldCode.MAP);
              const mapSearchField = {
                fieldCode: FieldCode.MAP,
                operatorValues: shapesValues.map((values: Array<string>) => ({
                  operatorLabel: null,
                  operator: 'POLYGON',
                  values,
                  valueType: 0
                }))
              };
              searchFields.push(mapSearchField);
            }
            if (this.premiumOptionSelected &&
              this.premiumOptionSelected.operatorValues &&
              this.premiumOptionSelected.operatorValues.length &&
              this.premiumOptionSelected.operatorValues[0].values.length) {
              this.isFormEmpty = false;
              this.isFormEmpty$.next(false);
              searchFields.push(this.premiumOptionSelected);
            }

            return searchFields;
          }
        })
      );
  }

  public search(isThisForCount?: boolean): void {
    this.getDataFromTemplate(true)
      .pipe(take(1))
      .subscribe((searchFields: Array<SearchField>) => {
        if (this.premiumOptionSelected &&
          this.premiumOptionSelected.operatorValues &&
          this.premiumOptionSelected.operatorValues.length &&
          this.premiumOptionSelected.operatorValues[0].values.length) {
          this.isFormEmpty = false;
          this.isFormEmpty$.next(false);
        }

        if (!isThisForCount) {
          let crossFields;

          if (this.crossFields) {
            crossFields = searchFields.filter((field) => {
              return this.crossFields.find((i) => i !== field.fieldCode);
            });
          }

          let fieldsForSearch = crossFields ? crossFields : searchFields;

          const premiumField = {...fieldsForSearch.find((i) => PremiumFieldCodes.includes(i.fieldCode))};

          if (premiumField && Object.keys(premiumField).length) {
            fieldsForSearch = this.optimizePremiumField(premiumField, fieldsForSearch);
          }

          this.store$.dispatch(new MySearch({ searchFields: fieldsForSearch }));
          this.store$.dispatch(new SetShowSelected(false));
          this.mixpanelService.track('Search - Search Executed', {
            searchType: 'mySearch',
            isSavedSearch: this.selectedSearch !== 'My Search Default',
            searchParameters: (fieldsForSearch).map((x) => x.fieldCode),
            countyList: this.countyList,
            isApiSearch: false
          });
        }
      });
    this.store$.dispatch(new RemoveCurrentPropertyIndex());
    this.getDataFromTemplate(false)
      .pipe(take(1))
      .subscribe((searchFields: Array<SearchField>) => this.store$.dispatch(new SaveValuesOfMySearch(searchFields)));
  }
  public clearSearch(event: Event): void {
    event.preventDefault();
    this.store$.dispatch(new ClearAllPremium(true));
    this.premiumOptionSelected = null;
    this.lastOpenedTemplateCode = null;
    this.store$.dispatch(new SaveValuesOfMySearch([]));
    this.store$.pipe(
      select(selectMySearchFieldsData),
      filter((arr: Array<any>) => Array.isArray(arr) && arr.length === 0),
      take(1)
    ).subscribe(() => {
      // Re-open the template (this will recreate the form controls)
      if (this.searchTemplate) {
        this.openSearch(this.searchTemplate, true);
      }
      // Notify parent after internal handling to avoid triggering internal subscription prematurely
      this.clearSearchResults.emit();
    });
  }

  public openEditSearchModal(isNewSearch: boolean = false): void {
    this.mixpanelService.track('Search - Customize Search Option');
    if (isNewSearch) {
      this.store$.dispatch(new SetSelectedMySearchModal(null));
      this.dialog.open(EditSearchComponent, {
        panelClass: ModalType.LARGE,
      });
      return;
    }

    this.selectedSearch$
      .pipe(
        switchMap((t: Template) => this.addShapeSearchFieldToTemplate(t)),
        take(1),
      )
      .subscribe((t: Template) => {
        this.store$.dispatch(new SetSelectedMySearch(t));
        this.dialog.open(EditSearchComponent, {
          panelClass: ModalType.LARGE,
        });
      });
  }

  public openMySearch(item: Template): void {
    this.savedSearchSelected = item.label;
    this.selectedSearch$
      .pipe(
        first(),
        withLatestFrom(this.getDataFromTemplate(false)),
        switchMap(([template, searchFields]: [Template, Array<SearchField>]) => {
          const needToUpdate = this.notSaved(template, searchFields, this.mySearchForm.dirty);
          const withValues = this.checkIfSearchWithValues(searchFields);

          if (needToUpdate) {
            return this.confirmationService.openMySearchConfirmationModal({
              description: template.label,
              type: template.type ? template.type : null,
              withValues,
              templateCode: template.templateCode,
              templateNames: this.templateNames
            })
              .pipe(map((modalData: ConfModalResponse) => ({modalData, template})));
          }

          return of({modalData: null, template});
        })
      )
      .subscribe(({modalData, template}) => {
        if (!modalData) {
          this.store$.dispatch(new SetSelectedMySearch(item));
          this.store$.dispatch(new SetMySearchLastLoadedTemplate(item));
          return;
        }

        if (modalData.name && modalData.type) {
          this.updateOrSaveTemplate(modalData, template);
          return;
        }
      });
  }

  private notSaved(template: Template, searchFields: Array<SearchField>, isFormChanged: boolean): boolean {
    if (template && !template.templateCode) {
      return true;
    }

    const oldTemplate = this.mySearchList
      .find((existingTemplate: Template) =>
        existingTemplate.templateCode === template.templateCode);

    if (!oldTemplate) {
      return true;
    }

    const withValues = this.checkIfSearchWithValues(searchFields);
    if (oldTemplate.type === TemplateType.FORM && withValues && isFormChanged ||
      oldTemplate.type === TemplateType.SEARCH && !withValues && isFormChanged ||
      oldTemplate && searchFields.length !== oldTemplate.dataElementList.length) {
      return true;
    }

    if (withValues && oldTemplate && oldTemplate.type === TemplateType.SEARCH) {
      return searchFields.some((searchField: SearchField) => {

        const field = oldTemplate.dataElementList
          .find((dataElement: DataElement) => dataElement.fieldCode === searchField.fieldCode);

        if (!field) {
          return true;
        }

        return field.operatorValueList.some((operator: OperatorValue, index: number) => {
          const formFieldValues = searchField.operatorValues[index] && searchField.operatorValues[index].values || [''];

          let templateValues: Array<string | number>;
          if (field.fieldCode === FieldCode.MAP && operator.values) {
            templateValues = MapUtils.getNormalizedShapeValues(operator.values);
          } else {
            templateValues = operator.values || [''];
          }

          if ((templateValues[0] || '') !== formFieldValues[0]) {
            return true;
          }

          if (templateValues.length > 1) {
            return (templateValues[1] || '') !== formFieldValues[1];
          }

          return false;
        });
      });
    }

    return false;
  }

  public removeShape(shape: Shape): void {
    this.store$.dispatch(new RemoveDrawingFigure(shape.id));
  }

  public getSearchFieldExpanded(searchField: DataElement): boolean {
    if (!this.crossFields) {
      return true;
    }

    const isCrossField = !!this.crossFields.find((i) => i === searchField.fieldCode);
    return !isCrossField;
  }

  private createForm(): void {
    // Complete the previous form subscription before creating a new one
    this.formDestroy$.next();

    this.mySearchForm = this.fb.group({});

    // Validity stream — no debounce, fires on every status change.
    // async pipe in the template triggers CD immediately (OnPush-safe).
    this.isFormValid$ = this.mySearchForm.statusChanges.pipe(
      startWith(this.mySearchForm.status),
      map((status) => status === 'VALID'),
      distinctUntilChanged(),
      takeUntil(this.formDestroy$),
      takeUntilDestroyed(this.destroyRef)
    );

    // Empty-state stream — no debounce, fires on every keystroke.
    // Pushes into isFormEmpty$ so the async pipe triggers CD immediately (OnPush-safe).
    combineLatest([
      this.mySearchForm.valueChanges.pipe(startWith(this.mySearchForm.value)),
      this.mapShapes$
    ])
      .pipe(
        map(([_, shapes]) => !this.hasSearchCriteria(shapes)),
        distinctUntilChanged(),
        takeUntil(this.formDestroy$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((empty) => {
        this.isFormEmpty = empty;
        this.isFormEmpty$.next(empty);
      });

    // Only react to form value changes and map shape changes.
    // Counties are NOT needed here — they don't affect form field extraction.
    combineLatest([
      this.mySearchForm.valueChanges,
      this.mapShapes$
    ])
      .pipe(
        // Debounce FIRST — prevents the expensive getDataFromTemplate from running on every keystroke
        debounceTime(SEARCH_RESULTS_COUNT_DEBOUNCE_TIME),
        takeUntil(this.formDestroy$),
        takeUntilDestroyed(this.destroyRef),
        // Now do the expensive work only once per debounced batch
        switchMap(([_, shapes]) => this.getDataFromTemplate(true)
          .pipe(map((fields: Array<SearchField>) => ([shapes, fields]))))
      )
      .subscribe(([shapes, fields]: [Array<Shape>, Array<SearchField>]) => {
        this.isFormEmpty = !(shapes.length || fields?.length);
        this.crossFields = null;

        if (this.isFormEmpty) {
          this.currentLayerOperatorValueKey = null;
          this.store$.dispatch(new ClearSearchResultsCount());
          return;
        }

        // Save form values to store (debounced, so only fires once)
        this.store$.dispatch(new SaveValuesOfMySearch(fields));

        fields.forEach((field) => {
          const crossFields = PremiumCrossFieldCodes.get(field.fieldCode as FieldCode);
          if (crossFields) {
            this.crossFields = crossFields;
          }
        });

        this.cd.markForCheck();

        const fieldString = JSON.stringify(fields);
        if (this.currentLayerOperatorValueKey !== fieldString) {
          this.currentLayerOperatorValueKey = fieldString;
          let filteredFields;

          if (this.crossFields) {
            filteredFields = fields.filter((field) => {
              return this.crossFields.find((crossFieldCode) => crossFieldCode !== field.fieldCode);
            });
          }

          let fieldForCheck = (filteredFields ? filteredFields : fields);

          if (this.lastSelectedPremiumOption) {
            if (fieldForCheck.length === 1 && fieldForCheck[0].fieldCode === this.lastSelectedPremiumOption.fieldCode) {
              fieldForCheck = this.lastSelectedPremiumOption.operatorValues.length ? fieldForCheck : [];
            }

            const premiumField = {...fieldForCheck.find((i) => PremiumFieldCodes.includes(i.fieldCode))};

            if (premiumField && Object.keys(premiumField).length) {
              fieldForCheck = this.optimizePremiumField(premiumField, fieldForCheck);
            }
          }
          this.store$.dispatch(new GetSearchResultsCount(fieldForCheck));
        }
      });
  }

  private addShapeSearchFieldToTemplate(template: Template): Observable<Template> {
    return this.getDataFromTemplate(false)
      .pipe(
        switchMap((searchFields: Array<SearchField>) => {
          const templateCopy: Template = {...template};
          const withMapOperator = searchFields.find((sf: SearchField) => sf.fieldCode === FieldCode.MAP);

          templateCopy.dataElementList = templateCopy.dataElementList.map((dataElement: DataElement) => {
            const dataElemCopy = {...dataElement};
            const searchField = searchFields.find((field: SearchField) => dataElemCopy.fieldCode === field.fieldCode);
            dataElemCopy.operatorValueList = searchField?.operatorValues || dataElemCopy.operatorValueList || [];
            return dataElemCopy;
          });

          if (withMapOperator) {
            return this.store$
              .pipe(
                select(selectDrawingShapes),
                withLatestFrom(this.store$.select(selectSearchField(FieldCode.MAP))),
                take(1),
                map(([shapes, defaultMapSearchField]: [Array<Shape>, DataElement]) => {

                  templateCopy.dataElementList = [
                    ...templateCopy.dataElementList.filter((el: DataElement) => el.fieldCode !== FieldCode.MAP), {
                      ...defaultMapSearchField,
                      defaultOperator: 'POLYGON',
                      operatorValueList: withMapOperator.operatorValues,
                      customAttributeInfo: {
                        customAttrib2: JSON.stringify(shapes)
                      }
                    }];
                  return templateCopy;
                })
              );
          }
          return of(templateCopy);
        })
      );
  }

  public save(): void {
    this.selectedSearch$
      .pipe(
        withLatestFrom(this.getDataFromTemplate(false)),
        take(1),
        switchMap(([template, searchFields]: [Template, Array<SearchField>]) => {
          const withValues = this.checkIfSearchWithValues(searchFields);

          return this.confirmationService.openMySearchConfirmationModal({
            title: 'Save Search',
            description: template.label,
            type: template.type ? template.type : null,
            withValues,
            templateCode: template.templateCode,
            templateNames: this.templateNames,
            messageType: null,
            messageTitle: null
          }).pipe(
            filter((modalData: ConfModalResponse) => !!(modalData && modalData.type && modalData.name)),
            map((confModalResponse: ConfModalResponse) => ([confModalResponse, template, searchFields]))
          );
        })
      ).subscribe(([confModalResponse, updateTemplate]: [ConfModalResponse, Template]) =>
      this.updateOrSaveTemplate(confModalResponse, updateTemplate));
  }

  private updateOrSaveTemplate(confModalResponse: ConfModalResponse, updateTemplate: Template): void {
    let needToUpdate = updateTemplate;
    if (confModalResponse.updateExisting) {
      needToUpdate = this.mySearchList
        .find((t: Template) => t.label === confModalResponse.name && t.type === confModalResponse.type) || updateTemplate;
    }

    const save = !confModalResponse.updateExisting && (
      updateTemplate && !updateTemplate.templateCode ||
      updateTemplate.type !== confModalResponse.type ||
      confModalResponse.name !== updateTemplate.label);

    if (save && (confModalResponse.type === TemplateType.FORM)) {
      this.store$.dispatch(new SaveCustomMySearch({
        ...updateTemplate,
        dataElementList: updateTemplate.dataElementList.reduce((prev: Array<DataElement>, curr: DataElement) => {
          if (curr.fieldCode === FieldCode.MAP) {
            return prev;
          }
          const copyDataElement = {...curr};
          copyDataElement.operatorValueList = [];
          prev.push(copyDataElement);
          return prev;
        }, []),
        type: confModalResponse.type,
        customTemplate: true,
        label: confModalResponse.name,
        templateCode: null
      }));
      return;
    }

    this.addShapeSearchFieldToTemplate(needToUpdate)
      .pipe(take(1))
      .subscribe((template: Template) => {
        if (save) {
          this.store$.dispatch(new SaveCustomMySearch({
            ...template,
            type: confModalResponse.type,
            customTemplate: true,
            label: confModalResponse.name,
            templateCode: null
          }));
        } else {
          this.store$.dispatch(new UpdateCustomMySearch(template));
          this.store$.dispatch(new SetSelectedMySearch(template));
        }
      });
  }

  private openSearch(item: Template, clearValues: boolean = false): void {
    if (!item) {
      return;
    }
    this.savedSearchSelected = item.label;
    this.getDefaultOperatorValueList(item);

    const templateKey = this.getTemplateRenderKey(item);
    const isNewTemplate = this.lastOpenedTemplateCode !== templateKey;
    this.lastOpenedTemplateCode = templateKey;

    this.store$.pipe(
      select(selectMySearchFieldsData),
      take(1)
    ).subscribe((fieldsData: Array<SearchField>) => {
      const searchConfigList = item.dataElementList;
      const shouldRebuildForm = isNewTemplate || clearValues || this.hasFormStructureChanged(searchConfigList);

      if (shouldRebuildForm) {
        this.createForm();
      } else {
        // Same template re-opened (e.g. map layer change) — no need to rebuild controls
        this.searchFields$.next(searchConfigList);
        return;
      }

      searchConfigList.forEach((fieldConfig: DataElement) => {
        let value: Array<string | number | Array<string | number>> = [''];
        const operator = fieldConfig.defaultOperator;
        const isDateField = fieldConfig.displayInfo.renderId === RENDER_ID.CALENDAR_TOOL;
        const searchFieldWithData = fieldsData &&
          fieldsData.find((field: SearchField) => field.fieldCode === fieldConfig.fieldCode);
        const formArray: Array<UntypedFormGroup> = [];

        if (fieldConfig.fieldCode === FieldCode.MAP) {
          return;
        }

        if (clearValues) {
          // Force empty controls regardless of saved/template operator values
          if (this.checkIfMultiField(fieldConfig)) {
            formArray.push(this.createFormGroupElement(operator, [[]], fieldConfig));
          } else {
            formArray.push(this.createFormGroupElement(operator, ['', ''], fieldConfig));
          }
        } else if (searchFieldWithData) {
          if (this.checkIfMultiField(fieldConfig)) {
            const multiValue: Array<string | number> = [];
            searchFieldWithData.operatorValues.forEach((operatorValue: OperatorValue) =>
              multiValue.push(operatorValue.values ? operatorValue.values[0] : ''));
            value = [multiValue];
            formArray.push(this.createFormGroupElement(operator, value, fieldConfig));
          } else {
            searchFieldWithData.operatorValues.forEach((operatorValue: OperatorValue) => {
              const values = isDateField ? this.formatDateValue(operatorValue.values) : operatorValue.values;
              formArray.push(this.createFormGroupElement(operatorValue.operator, values, fieldConfig));
            });
          }
        } else {
          if (this.checkIfMultiField(fieldConfig)) {
            const multiValue: Array<string | number> = [];
            fieldConfig.operatorValueList.forEach((operatorVal: OperatorValue) =>
              multiValue.push(operatorVal.values ? operatorVal.values[0] : ''));
            value = [multiValue];
            formArray.push(this.createFormGroupElement(operator, value, fieldConfig));
          } else {
            if (fieldConfig.operatorValueList[0] && fieldConfig.operatorValueList[0].values) {
              fieldConfig.operatorValueList.forEach((operatorValue: OperatorValue) => {
                const values = isDateField ? this.formatDateValue(operatorValue.values) : operatorValue.values;
                formArray.push(this.createFormGroupElement(operatorValue.operator, values, fieldConfig));
              });
            } else {
              formArray.push(this.createFormGroupElement(operator, ['', ''], fieldConfig));
            }
          }
        }

        this.mySearchForm.setControl(fieldConfig.fieldCode, this.fb.array(formArray));
      });

      this.searchFields$.next(searchConfigList);
    });
  }

  private hasFormStructureChanged(searchConfigList: Array<DataElement>): boolean {
    if (!this.mySearchForm) {
      return true;
    }

    const configuredFieldCodes = searchConfigList
      .filter((dataElement: DataElement) => dataElement.fieldCode !== FieldCode.MAP)
      .map((dataElement: DataElement) => dataElement.fieldCode)
      .sort();
    const currentFieldCodes = Object.keys(this.mySearchForm.controls)
      .filter((fieldCode: string) => fieldCode !== FieldCode.MAP)
      .sort();

    return configuredFieldCodes.length !== currentFieldCodes.length ||
      configuredFieldCodes.some((fieldCode: string, index: number) => fieldCode !== currentFieldCodes[index]);
  }

  private getDefaultOperatorValueList(template: Template): void {
    const allFieldCodes = template.dataElementList.map((dataElement: DataElement) => dataElement.fieldCode);
    this.store$.pipe(
      select(selectSearchFieldsArray(allFieldCodes as Array<FieldCode>)),
      take(1)
    ).subscribe((dataElements: Array<DataElement>) => {
      dataElements.forEach((element: DataElement) => {
        this.defaultOperatorValueListMap[element.fieldCode] = element.operatorValueList;
      });
    });
  }

  private getTemplateRenderKey(template: Template | null): string {
    if (!template) {
      return '';
    }

    return JSON.stringify({
      templateCode: template.templateCode || '',
      label: template.label || '',
      type: template.type || '',
      dataElementList: (template.dataElementList || []).map((element: DataElement) => ({
        fieldCode: element.fieldCode,
        defaultOperator: element.defaultOperator || '',
        operatorValueList: (element.operatorValueList || []).map((operatorValue: OperatorValue) => ({
          operator: operatorValue.operator,
          values: operatorValue.values || []
        }))
      }))
    });
  }

  public onOperatorValueChanged(control: AbstractControl, dataElement: DataElement): void {
    const valueControl: AbstractControl = control.get('values');
    const validators = RlstValidators.getValidators(
      dataElement.validationInfoList || [], control.get('operator').value
    );
    valueControl.setValidators(validators);
    valueControl.get('valueTo').setValue('');
  }

  public onFieldAdded(control: AbstractControl, searchField: DataElement): void {
    const valueFromControl = control.get(['values', 'valueFrom']);
    const valueToControl = control.get(['values', 'valueTo']);
    const searchList: UntypedFormArray = this.mySearchForm.get(searchField.fieldCode) as UntypedFormArray;
    searchList.push(new UntypedFormGroup({
      operator: new UntypedFormControl(control.get('operator').value),
      values: new UntypedFormGroup({
        valueFrom: new UntypedFormControl(valueFromControl.value,
          RlstValidators.getSingleFieldValidator(searchField.validationInfoList || [])),
        valueTo: new UntypedFormControl(valueToControl.value,
          RlstValidators.getSingleFieldValidator(searchField.validationInfoList || []))
      }, RlstValidators.getValidators(searchField.validationInfoList || [], control.get('operator').value))
    }));
    valueFromControl.setValue('');
    valueToControl.setValue('');
    this.mixpanelService.track('Search - Or Field Added', {
      searchType: 'mySearch',
      fieldName: searchField.fieldCode
    });
  }

  private formatValues(value: string | Date): string {
    if (value instanceof Date) {
      return this.datePipe.transform(value, 'yyyy/MM/dd').split('/').join('');
    }
    return value;
  }

  private formatLookUpValues(value: string): string {
    // split by comma that is NOT followed by space
    // splitting 'HIGHLANDS, THE,HIGHLANDS, THE' will output ["HIGHLANDS, THE", "HIGHLANDS, THE"]
    if ((value.match(/,/g) || []).length === 1) {
      return value && value.includes(',') ? value.split(/,(?!\s+)/)[0] : value || '';
    } else {
      return value && value.includes(MULTI_MODAL_MY_SEARCH) ? value.split(MULTI_MODAL_MY_SEARCH)[0] : value || '';
    }
  }

  private formatDateValue(value: Array<string | number>): Array<string | number | Date> {
    return value && value.length ?
      value.map((el: string | number) => el && (typeof el === 'string') ? new Date(StringUtils.formatDate(el)) : el) :
      value;
  }

  /** Cheap sync check used by the no-debounce empty-state stream. */
  private hasSearchCriteria(shapes: Array<Shape>): boolean {
    if (shapes?.length) {
      return true;
    }
    if (this.premiumOptionSelected?.operatorValues?.some(
      (ov: OperatorValue) => ov.values?.some((v) => this.hasValue(v)))) {
      return true;
    }
    return Object.values(this.mySearchForm.controls).some((control: AbstractControl) => {
      const arr = control as UntypedFormArray;
      return arr.controls?.some((fg: AbstractControl) =>
        this.hasValue(fg.get(['values', 'valueFrom'])?.value) ||
        this.hasValue(fg.get(['values', 'valueTo'])?.value)
      );
    });
  }

  private hasValue(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.some((item) => this.hasValue(item));
    }
    return value !== null && value !== undefined && value !== '';
  }

  private checkIfSearchWithValues(searchFields: Array<SearchField>): boolean {
    return searchFields.some((curr: SearchField) =>
      curr.fieldCode === FieldCode.MAP ||
      curr.operatorValues.some((operatorValue: OperatorValue) =>
        operatorValue.values[0] !== ''));
  }

  private checkIfMultiField(dataElement: DataElement): boolean {
    return ((dataElement.displayInfo.renderId === RENDER_ID.SINGLE_SELECT) && dataElement.numberOfSearchValues !== 1) ||
      dataElement.displayInfo.renderId === RENDER_ID.DIALOGBOX_LOOKUP ||
      dataElement.displayInfo.renderId === RENDER_ID.DIALOGBOX_LOOKUP_WITH_DATA_LOAD ||
      dataElement.displayInfo.renderId === RENDER_ID.MULTI_SELECT_SEARCH_LOOKUP;
  }

  private createFormGroupElement(operator: string,
                                 values: Array<string | number | Date | Array<string | number | Date>>,
                                 fieldConfig: DataElement): UntypedFormGroup {

    return this.fb.group({
      operator,
      values: new UntypedFormGroup({
        valueFrom: new UntypedFormControl(
          values[0] || '',
          RlstValidators.getSingleFieldValidator(
            this.checkIfMultiField(fieldConfig) ? [] : fieldConfig.validationInfoList || [])
        ),
        valueTo: new UntypedFormControl(
          values[1] || '',
          RlstValidators.getSingleFieldValidator(fieldConfig.validationInfoList || []))
      }, RlstValidators.getValidators(fieldConfig.validationInfoList || [], operator))
    });
  }

  private getMySearchesSortedList(): void {
    combineLatest(
      [
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_MY_SEARCH_DEFAULT))),
        this.store$.pipe(select(selectMySearchesTemplates)),
        this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_PREMIUM_SEARCH)))
      ]
    ).pipe(
      map(([defaultTemplate, templates, premiumTemplate]: [Template, Array<Template>, Template]) => {
        this.premiumMapTemplate = premiumTemplate;
        this.templateNames = [defaultTemplate, ...templates].map(({label, type}: Template) => ({label, type}));
        return [defaultTemplate, ...templates].sort((a: Template, b: Template) =>
          new Date(b.modifiedDate).getTime() - new Date(a.modifiedDate).getTime());
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((list: Array<Template>) => this.mySearchList = list);
  }

  public onSelectPremiumOption(premOption: SearchField) {
    this.lastSelectedPremiumOption = premOption;
    this.store$.dispatch(new ClearAllPremium(false));
    if (premOption && premOption.operatorValues && premOption.operatorValues.length && premOption.operatorValues[0].values.length) {
      this.premiumOptionSelected = JSON.parse(JSON.stringify(premOption));
    } else {
      this.isFormEmpty = true;
      this.isFormEmpty$.next(true);
      this.premiumOptionSelected = null;
    }

    this.search(true);
  }

  private optimizePremiumField(premiumField: SearchField, fieldsForSearch: Array<SearchField>): Array<SearchField> {
    const premiumOperatorValues = [...premiumField.operatorValues];
    const optimizedOperatorValues: Array<OperatorValue> = [];
    const nonOptimizedOperatorValues: Array<OperatorValue> = [];
    let queueIndex: number = null;

    const sortedPremiumField = {
      ...premiumField, operatorValues: premiumOperatorValues.sort((a, b) => {
        return (a.lookUpIndex - b.lookUpIndex);
      })
    };

    if (sortedPremiumField) {
      sortedPremiumField.operatorValues.forEach((operatorValue) => {
        const {lookUpIndex, ...value} = operatorValue;

        if (value.operator === SearchOperator.BETWEEN) {
          if (!optimizedOperatorValues.length) {
            optimizedOperatorValues.push(value);
            queueIndex = lookUpIndex;
            return;
          }

          if (queueIndex !== null && queueIndex + 1 === lookUpIndex) {
            let lastOptimizedOperatorValues = optimizedOperatorValues[optimizedOperatorValues.length - 1];
            lastOptimizedOperatorValues = {
              ...lastOptimizedOperatorValues,
              values: [lastOptimizedOperatorValues.values[0], value.values[1]]
            };
            optimizedOperatorValues[optimizedOperatorValues.length - 1] = lastOptimizedOperatorValues;
          } else {
            optimizedOperatorValues.push(value);
          }

          queueIndex = lookUpIndex;
        } else {
          nonOptimizedOperatorValues.push(value);
        }
      });
    }

    return fieldsForSearch.map((searchField) => {
      if (PremiumFieldCodes.includes(searchField.fieldCode)) {
        return {...searchField, operatorValues: [...nonOptimizedOperatorValues, ...optimizedOperatorValues]};
      }
      return searchField;
    });
  }
}
